import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const NAVY = '#0076A3';
const GOLD = '#00A0E0';
const TOOLTIP_STYLE = { background: '#fff', border: '1px solid #ECE7DE', borderRadius: 6 };
const AXIS_TICK = { fill: '#94A3B8', fontSize: 11 };
const BAR_RADIUS = [3, 3, 0, 0];
const LEGEND_STYLE = { fontSize: '0.75rem' };

const GEO_COMPLIANCE = [
  { region: 'India', regulation: 'DPDP Act 2023', details: 'Biometric consent · Data-subject rights · Grievance officer' },
  { region: 'EU', regulation: 'GDPR', details: 'Right to erasure · Lawful basis · Consent management' },
  { region: 'USA', regulation: 'BIPA + AIVIA (Illinois)', details: 'Biometric data · AI interview consent · 30-day deletion' },
  { region: 'UAE / Singapore', regulation: 'PDPL / PDPA', details: 'Cross-border safeguards · Purpose limitation' },
];

const SectionTitle = ({ children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.0625rem', fontWeight: 700, color: NAVY, margin: 0 }}>{children}</h2>
    <div style={{ width: 40, height: 3, background: GOLD, marginTop: 6 }} />
  </div>
);

const ComingSoon = () => (
  <span style={{ fontSize: '0.5625rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#92722A', background: 'rgba(14, 165, 233,0.22)', border: '1px solid rgba(14, 165, 233,0.5)', borderRadius: 4, padding: '0.0625rem 0.375rem' }}>Coming soon</span>
);

export default function AnalyticsPage() {
  const [cc, setCc] = useState(null);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [trendWeeks, setTrendWeeks] = useState(8);
  const [trendLoading, setTrendLoading] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [ccRes, sRes, tRes] = await Promise.all([
        dashboardAPI.getCommandCenter(), dashboardAPI.getStats(), dashboardAPI.getHiringTrends(8),
      ]);
      setCc(ccRes.data);
      setStats(sRes.data);
      setTrends(tRes.data);
      if (isRefresh) toast.success('Analytics refreshed');
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  const loadTrends = useCallback(async (weeks) => {
    setTrendWeeks(weeks);
    setTrendLoading(true);
    try {
      const res = await dashboardAPI.getHiringTrends(weeks);
      setTrends(res.data);
    } catch (err) {
      toast.error('Failed to load trends');
    } finally { setTrendLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }}>Loading analytics...</div>;
  if (!cc) return null;

  const { funnel, productivity, feature_adoption, adoption_pct, candidate_pipeline, scorecard } = cc;
  const applied = funnel?.[0]?.count || 0;
  const joined = funnel?.[funnel.length - 1]?.count || 0;
  const conversionRate = applied ? Math.round((joined / applied) * 100) : 0;
  const scoreDist = stats?.score_distribution
    ? Object.entries(stats.score_distribution).map(([range, count]) => ({ range, count }))
    : [];
  const conversionByStage = (funnel || []).map(f => ({ stage: f.stage, conversion: f.conversion_pct }));

  const KPIS = [
    { label: 'Total Candidates', value: candidate_pipeline.total, color: NAVY, sub: `${candidate_pipeline.new} added this week` },
    { label: 'Conversion Rate', value: `${conversionRate}%`, color: '#059669', sub: 'Applied → Joined' },
    { label: 'Time to Hire', value: trends?.avg_time_to_hire_days != null ? `${trends.avg_time_to_hire_days}d` : '—', color: '#00A0E0', sub: trends?.avg_time_to_hire_days != null ? `Across ${trends.hires_measured} hire(s)` : 'No hires yet' },
    { label: 'Resumes Screened', value: productivity.resumes_screened, color: '#F2600C', sub: 'AI-scored by CHIRE' },
    { label: 'Hours Saved', value: `${productivity.hours_saved}h`, color: '#D97706', sub: 'Estimated automation gain' },
    { label: 'Feature Adoption', value: `${adoption_pct}%`, color: NAVY, sub: 'Modules in active use' },
  ];

  return (
    <div data-testid="analytics-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">Hiring performance, conversion, and platform value — powered by your live data.</p>
        </div>
        <button data-testid="analytics-refresh-btn" onClick={() => load(true)} disabled={refreshing}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 4, padding: '0.625rem 1.125rem', fontSize: '0.8125rem', fontWeight: 600, cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.7 : 1 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: '2rem' }}>
        {KPIS.map(({ label, value, color, sub }) => (
          <div key={label} className="kpi-tile" data-testid={`analytics-kpi-${label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="kpi-label">{label}</div>
            <div className="kpi-value" style={{ color }}>{value}</div>
            <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.25rem' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Funnel + Score distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }} className="cc-funnel-grid">
        <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.5rem' }}>
          <SectionTitle>Hiring Funnel</SectionTitle>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
              <XAxis dataKey="stage" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [`${v} (${p.payload.conversion_pct}% conv.)`, 'Candidates']} />
              <Bar dataKey="count" fill={NAVY} radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.5rem' }}>
          <SectionTitle>Candidate Score Distribution</SectionTitle>
          {scoreDist.length > 0 ? (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={scoreDist}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
                <XAxis dataKey="range" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill={GOLD} radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ textAlign: 'center', color: '#94A3B8', padding: '4rem' }}>No scored candidates yet</div>}
        </Card>
      </div>

      {/* Conversion trend + Feature adoption */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }} className="cc-funnel-grid">
        <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.5rem' }}>
          <SectionTitle>Stage Conversion %</SectionTitle>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={conversionByStage}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
              <XAxis dataKey="stage" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} domain={[0, 100]} unit="%" />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}%`, 'Conversion']} />
              <Line type="monotone" dataKey="conversion" stroke={NAVY} strokeWidth={2.5} dot={{ fill: GOLD, r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.5rem' }}>
          <SectionTitle>Feature Adoption — {adoption_pct}%</SectionTitle>
          {feature_adoption.map(f => (
            <div key={f.feature} style={{ marginBottom: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#334155', marginBottom: 4 }}>
                <span>{f.feature}</span><span style={{ fontWeight: 700, color: f.used ? '#16A34A' : '#94A3B8' }}>{f.used ? `${f.count} used` : 'Not used'}</span>
              </div>
              <div style={{ height: 6, background: '#EEF2F6', borderRadius: 3 }}>
                <div style={{ width: f.used ? '100%' : '4%', height: '100%', background: f.used ? NAVY : '#CBD5E1', borderRadius: 3 }} />
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* Hiring trends over time */}
      <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.5rem', marginBottom: '2rem' }} data-testid="analytics-trends">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <SectionTitle>Hiring Trends — last {trendWeeks} weeks</SectionTitle>
          <div style={{ display: 'flex', gap: '0.375rem', opacity: trendLoading ? 0.6 : 1 }} data-testid="trend-range-selector">
            {[4, 8, 12, 26].map(w => (
              <button key={w} data-testid={`trend-range-${w}`} onClick={() => loadTrends(w)} disabled={trendLoading || w === trendWeeks}
                style={{
                  padding: '0.3125rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, borderRadius: 4, cursor: w === trendWeeks ? 'default' : 'pointer',
                  border: `1px solid ${w === trendWeeks ? NAVY : '#CBD5E1'}`,
                  background: w === trendWeeks ? NAVY : '#fff', color: w === trendWeeks ? '#fff' : '#475569',
                }}>
                {w}w
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trends?.series || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
            <XAxis dataKey="week" tick={AXIS_TICK} interval="preserveStartEnd" />
            <YAxis tick={AXIS_TICK} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Line type="monotone" dataKey="added" name="Candidates added" stroke={NAVY} strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="advanced" name="Stage advances" stroke="#00A0E0" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="hired" name="Hires" stroke="#16A34A" strokeWidth={2} dot={{ fill: GOLD, r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Compliance alignment */}
      <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.5rem', marginBottom: '2rem' }}>
        <SectionTitle>Data-Protection Alignment</SectionTitle>
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '-0.5rem', marginBottom: '1.25rem' }}>CHIRE is designed to support these frameworks. This reflects product capabilities, not a certification.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {GEO_COMPLIANCE.map(gc => (
            <div key={gc.region} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1rem', borderRadius: 6, border: '1px solid #EEF2F6', background: '#FAFBFC' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: NAVY }}>{gc.region} — {gc.regulation}</div>
                <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>{gc.details}</div>
              </div>
              <span style={{ padding: '0.25rem 0.75rem', borderRadius: 999, background: 'rgba(240, 80, 0,0.06)', color: NAVY, fontSize: '0.6875rem', fontWeight: 600 }}>Designed to support</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Coming soon */}
      <Card style={{ background: '#FBFCFD', border: '1px dashed #CBD5E1', borderRadius: 6, padding: '1.5rem' }} data-testid="analytics-coming-soon">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.75rem' }}>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 700, color: NAVY, margin: 0 }}>Advanced Analytics — Coming Soon</h2>
          <ComingSoon />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem 1.5rem' }}>
          {['Cost-per-Hire (needs spend data)', 'Source Effectiveness (needs source tracking)', 'Offer Acceptance Rate', 'Recruiter & team benchmarking', 'Quality-of-hire scoring'].map(it => (
            <div key={it} style={{ fontSize: '0.75rem', color: '#94A3B8', padding: '0.125rem 0' }}>• {it}</div>
          ))}
        </div>
      </Card>
    </div>
  );
}
